import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import AggregatePercentageChart from './AggregatePercentageChart';
import { selectedPracticeArea } from '../appraisalComparisonCache';
import { useReactiveVar } from '@apollo/client';
import { FILTER_TYPES_KEYS } from '../../../constants/appraisal-comparison-filter-types';
import AggregatePercentageOrgVsIndChart from './AggregatePercentageOrgVsIndChart';
import { getPercentageRangeIndex } from './shared/services/getPercentageRangeIndex';
import { defaultAggregatePercentageModel } from './shared/constants/DefaultAggregatePercentageModel';
import { getScaleDataByIndustryTotal } from './shared/services/getScaleDataByIndustryTotal';
import SkeletonWrapper from '../../shared/components/skeleton/SkeletonWrapper';

/**
 * @typedef {object} AggregatePercentageSectionProps
 * @property {string} [selectedComparisonDataKey]
 * @property {Object} [orgReportData]
 * @property {Array<Object>} [unitAppraisalData]
 * @property {Object} [industryViewReportData]
 * @property {number} [selectedProgramYearValue]
 * @property {(data: import('recharts').BarRectangleItem, dataKey: string) => void} handleAggregatePercentageClick
 * @property {boolean} loading
 */

/**
 * @param {AggregatePercentageSectionProps} props
 */
const AggregatePercentageSection = props => {
  const {
    unitAppraisalData,
    orgReportData,
    industryViewReportData,
    selectedProgramYearValue,
    handleAggregatePercentageClick,
    loading,
    selectedComparisonDataKey = FILTER_TYPES_KEYS.Industry
  } = props;

  const [chartData, setChartData] = useState([]);
  const [chartDescription, setChartDescription] = useState('');
  const selectedPracticeAreaId = useReactiveVar(selectedPracticeArea);

  const [organizationTotal, setOrganizationTotal] = useState(0);
  const [industryTotal, setIndustryTotal] = useState(0);
  const [scaledData, setScaledData] = useState([]);

  useEffect(() => {
    if (chartData === null) return;

    const [total, industryTotal, scaledData] = getScaleDataByIndustryTotal(chartData);
    setOrganizationTotal(total);
    setIndustryTotal(industryTotal);
    setScaledData(scaledData);
  }, [chartData]);

  useEffect(() => {
    if (selectedComparisonDataKey == FILTER_TYPES_KEYS.OrgVsInd) {
      setChartDescription(
        'Displays the distribution of industry Aggregate Percentages (%) against ' +
          'the distribution of organization % for the selected PA and PGL.'
      );
    } else {
      setChartDescription(
        'Displays unit Aggregate Percentage (%) for the selected PA and PGL, as it compares to the distribution of' +
          (selectedComparisonDataKey === FILTER_TYPES_KEYS.Industry
            ? ' industry %.'
            : ' organization %.')
      );
    }
  }, [selectedComparisonDataKey]);

  useEffect(() => {
    if (!unitAppraisalData || !unitAppraisalData.length) {
      return;
    }
    const industryAppraisalViewReport = get(
      industryViewReportData,
      'appraisalComparisonView.PracticeAreas',
      []
    );
    const appraisalOrgs = get(orgReportData, 'organizationAppraisalReport.PracticeAreas', []);

    if (
      (industryAppraisalViewReport &&
        industryAppraisalViewReport.length > 0 &&
        selectedProgramYearValue != industryAppraisalViewReport[0].Year) ||
      (selectedComparisonDataKey == FILTER_TYPES_KEYS.Industry &&
        industryAppraisalViewReport.length == 0) ||
      (selectedComparisonDataKey == FILTER_TYPES_KEYS.Organization && appraisalOrgs.length == 0) ||
      (selectedComparisonDataKey == FILTER_TYPES_KEYS.OrgVsInd &&
        (appraisalOrgs.length == 0 || industryAppraisalViewReport.length == 0))
    ) {
      setChartData([]);
      return;
    }

    const selectedPAId =
      selectedPracticeAreaId == null
        ? unitAppraisalData[0].PracticeArea.Id
        : selectedPracticeAreaId;
    const aggregatePercantageChartData = defaultAggregatePercentageModel();

    const selectedPa = unitAppraisalData.find(x => x.PracticeArea.Id === selectedPAId);
    const selectedPaIndustry = industryAppraisalViewReport.find(x => x.Ids.includes(selectedPAId));
    const selectedPaOrg = appraisalOrgs.find(x => x.Ids.includes(selectedPAId));

    for (let i = 0; i < aggregatePercantageChartData.length; i++) {
      if (selectedPaIndustry) {
        aggregatePercantageChartData[i].I = selectedPaIndustry.AggregatePercentage[i].Results;
      }

      if (selectedPaOrg) {
        aggregatePercantageChartData[i].O = selectedPaOrg.AggregatePercentage[i].Results;
      }
      aggregatePercantageChartData[i].Id = selectedPAId;
    }

    const percantage = selectedPa?.AggregatePercentage;
    const rangeIndex = getPercentageRangeIndex(percantage);

    if (rangeIndex === undefined) {
      return;
    }

    const industryBarMidPoint = selectedPaIndustry
      ? selectedPaIndustry.AggregatePercentage[rangeIndex].Results / 2
      : null;
    const orgBarMidPoint = selectedPaOrg
      ? selectedPaOrg.AggregatePercentage[rangeIndex].Results / 2
      : null;

    aggregatePercantageChartData[rangeIndex].UnitAggregatePercentage = percantage;
    aggregatePercantageChartData[rangeIndex].IndMidPoint = industryBarMidPoint;
    aggregatePercantageChartData[rangeIndex].OrgMidPoint = orgBarMidPoint;

    setChartData(aggregatePercantageChartData);
  }, [
    selectedComparisonDataKey,
    selectedPracticeAreaId,
    unitAppraisalData,
    industryViewReportData,
    orgReportData,
    selectedProgramYearValue
  ]);

  return (
    <div id='appraisal-aggregate-percentage-section'>
      <p id='appraisal-aggregate-percentage-section-description'>{chartDescription}</p>
      <SkeletonWrapper
        show={loading}
        skeletonClassName='section-data-skeleton'
        renderComponent={() =>
          selectedComparisonDataKey == FILTER_TYPES_KEYS.OrgVsInd ? (
            <AggregatePercentageOrgVsIndChart
              total={organizationTotal}
              industryTotal={industryTotal}
              scaledData={scaledData}
              handleAggregatePercentageClick={handleAggregatePercentageClick}
            />
          ) : (
            <AggregatePercentageChart
              data={chartData}
              selectedDataKey={selectedComparisonDataKey}
              handleAggregatePercentageClick={handleAggregatePercentageClick}
            />
          )
        }
      />
    </div>
  );
};

export default AggregatePercentageSection;
